import { useMemo, useState } from 'react';
import { toActionBarBindingId, findEssenceAbilityForSkill, getWieldedLoadout } from '@sorrem/shared';
import type { UIPartyCharacter } from '@sorrem-client/types';
import { Button, InfoIcon, Panel, Separator } from '@sorrem-client/ui/components';
import { findSkillHotkeyIndex, withoutSkillHotkey } from '@sorrem-client/ui/shared/action-bar';
import { formatBindingLabel, useActionBarTargets, useConfirm, useCurrentPlayerCharacter, useEssenceSkillPoints, useInputBindings } from '@sorrem-client/ui/hooks';
import {
    buildSkillTreeModel,
    describeSkillTreeDial,
    describeSkillTreeGroup,
    describeSkillTreeNode,
    toDialSelectionId,
    toGroupSelectionId,
    resolveMartialTierName,
    PRAXIS_DEFINITION,
    SIGIL_SELECTION_ID,
    type SkillTreeDialModel,
    type SkillTreeGroupModel,
    type SkillTreeNodeModel,
} from '@sorrem-client/ui/features/SkillTree';
import styles from './SkillsDetailPanel.module.scss';

interface SkillsDetailPanelProps {
    /** The selected skill id, `dial:<branch>`, `group:<id>`, or the sigil (the branch overview); null while the panel is closed. */
    readonly selectedId: string | null;
    /** The skill waiting for the player to pick its action slot, or null. */
    readonly assigningSkillId: string | null;
    /** Starts (with a skill id) or cancels (null) picking an action slot for a skill. */
    readonly onAssigningSkillChange: (skillId: string | null) => void;
}

/** Picking an action slot for a skill, passed down to the Essence skill actions. */
interface HotkeyAssignmentProps {
    readonly assigningSkillId: string | null;
    readonly onAssigningSkillChange: (skillId: string | null) => void;
}

/**
 * The Skills tab's right panel: details of the selected skill, grouping root,
 * or dial, or an overview of the three branches when the sigil is selected. It only shows
 * while something is selected (see `CharacterHome`).
 */
export function SkillsDetailPanel({ selectedId, assigningSkillId, onAssigningSkillChange }: SkillsDetailPanelProps): JSX.Element {
    const { character: player } = useCurrentPlayerCharacter();
    // Keep showing the last selection while the panel slides out after it clears.
    const [shownId, setShownId] = useState(selectedId ?? SIGIL_SELECTION_ID);
    if (selectedId !== null && selectedId !== shownId) {
        setShownId(selectedId);
    }
    const model = useMemo(
        () => (player ? buildSkillTreeModel(player.skills, getWieldedLoadout(player), player.disciplines) : null),
        [player],
    );

    const selectedDial = model?.dials.find((dial) => toDialSelectionId(dial.placement.branch) === shownId) ?? null;
    const selectedNode = model?.nodes.find((node) => node.placement.id === shownId) ?? null;
    const selectedGroup = model?.groups.find((group) => toGroupSelectionId(group.placement.id) === shownId) ?? null;

    return (
        <Panel className={styles.skillsDetailPanel}>
            {model === null ? (
                <p className={styles.note}>No skill data available.</p>
            ) : selectedNode ? (
                <NodeDetail node={selectedNode} player={player} assigningSkillId={assigningSkillId} onAssigningSkillChange={onAssigningSkillChange} />
            ) : selectedGroup ? (
                <GroupDetail group={selectedGroup} />
            ) : selectedDial ? (
                <DialDetail dial={selectedDial} player={player} />
            ) : (
                <BranchOverview dials={model.dials} />
            )}
        </Panel>
    );
}

function NodeDetail({
    node,
    player,
    ...hotkeyAssignment
}: { node: SkillTreeNodeModel; player: UIPartyCharacter | null } & HotkeyAssignmentProps): JSX.Element {
    const details = describeSkillTreeNode(node);
    const isEssence = node.placement.branch === 'essence';
    return (
        <>
            <header className={styles.header}>
                <h2 className={styles.title}>{details.title}</h2>
                <span className={styles.meta}>{details.levelText}</span>
            </header>

            <Separator />

            {details.praxis && (
                <section className={styles.section}>
                    <div className={styles.rowLabel}>
                        <span className={styles.labelWithInfo}>
                            Praxis to next level
                            <InfoIcon ariaLabel="What is praxis?" popoverContent={PRAXIS_DEFINITION} />
                        </span>
                        <span>{details.praxis.current} / {details.praxis.needed}</span>
                    </div>
                    <div className={styles.bar} role="progressbar" aria-valuemin={0} aria-valuemax={details.praxis.needed} aria-valuenow={details.praxis.current}>
                        <div className={styles.barFill} style={{ width: `${Math.round(details.praxis.fraction * 100)}%` }} />
                    </div>
                </section>
            )}

            {details.ability !== undefined && (
                <section className={styles.section}>
                    <span className={styles.branchName}>{details.ability?.name ?? 'Ability'}</span>
                    {details.ability
                        ? (
                            <>
                                <p className={styles.blurb}>{details.ability.description}</p>
                                <p className={styles.status}>{details.ability.costText}</p>
                            </>
                        )
                        : <p className={styles.status}>This skill's ability is still being designed.</p>}
                </section>
            )}

            {isEssence && player && <EssenceSkillActions node={node} player={player} {...hotkeyAssignment} />}

            {details.perks.length > 0 && (
                <section className={styles.section}>
                    <span className={styles.branchName}>Perks</span>
                    <ol className={styles.perks}>
                        {details.perks.map((perk) => (
                            <li key={perk.name} className={perk.isActive ? styles.reached : undefined}>
                                <span className={styles.perkHeading}>
                                    <span>{perk.name}</span>
                                    <span>{perk.isActive ? 'Active' : perk.unlockText}</span>
                                </span>
                                <span className={styles.perkText}>{perk.description}</span>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            <dl className={styles.facts}>
                <dt>Decay risk</dt>
                <dd>{details.decayText}</dd>
                <dt>Status</dt>
                <dd>{details.activeText}</dd>
                <dt>Grows by</dt>
                <dd>{details.growsBy}</dd>
            </dl>
        </>
    );
}

/**
 * Raising an Essence skill with skill points, and putting its ability on the
 * action bar: "Assign to a hotkey" brings the loadout up to pick a slot.
 */
function EssenceSkillActions({
    node,
    player,
    assigningSkillId,
    onAssigningSkillChange,
}: { node: SkillTreeNodeModel; player: UIPartyCharacter } & HotkeyAssignmentProps): JSX.Element {
    const skillId = node.placement.id;
    const points = useEssenceSkillPoints(player);
    const { targets, setTargets } = useActionBarTargets();
    const { bindings } = useInputBindings();
    const cost = points.nextRingCost(skillId);
    const ability = findEssenceAbilityForSkill(skillId);
    const hotkeyIndex = findSkillHotkeyIndex(targets, skillId);
    const hotkeyBindingId = toActionBarBindingId(hotkeyIndex);
    const hotkeyLabel = hotkeyBindingId === undefined ? '' : formatBindingLabel(bindings, hotkeyBindingId);
    const isAssigning = assigningSkillId === skillId;

    return (
        <section className={styles.section}>
            <div className={styles.rowLabel}>
                <span>Skill points</span>
                <span>{points.availablePoints} unspent</span>
            </div>
            {cost === null
                ? <p className={styles.status}>Mastered.</p>
                : (
                    <Button
                        variant="secondary"
                        disabled={points.isSaving || points.availablePoints < cost}
                        onClick={() => { void points.raise(skillId); }}
                    >
                        Raise to ring {node.step + 1} - {cost} {cost === 1 ? 'point' : 'points'}
                    </Button>
                )}
            {ability && (
                <div className={styles.hotkeyRow}>
                    {isAssigning
                        ? <span className={styles.status}>Choose an action slot below.</span>
                        : hotkeyIndex >= 0 && <span className={styles.status}>On the action bar: key {hotkeyLabel}.</span>}
                    <Button variant="secondary" size="small" onClick={() => onAssigningSkillChange(isAssigning ? null : skillId)}>
                        {isAssigning ? 'Cancel' : hotkeyIndex >= 0 ? 'Change' : 'Assign to a hotkey'}
                    </Button>
                    {hotkeyIndex >= 0 && !isAssigning && (
                        <Button variant="link" size="small" onClick={() => setTargets(withoutSkillHotkey(targets, skillId))}>
                            Remove
                        </Button>
                    )}
                </div>
            )}
            {points.error && <p className={styles.status}>{points.error}</p>}
        </section>
    );
}

/** Veil Attunement's skill points, and returning them all to place again. */
function VeilPointsSummary({ player }: { player: UIPartyCharacter }): JSX.Element {
    const points = useEssenceSkillPoints(player);
    const { confirm } = useConfirm();
    const spentPoints = points.earnedPoints - points.availablePoints;

    const reset = async (): Promise<void> => {
        const confirmed = await confirm({
            title: 'Return your skill points?',
            description: `Every Essence skill goes back to ring 1 and its points return to you, for ${points.respecCost} banked essence.`,
            confirmLabel: 'Return them',
            cancelLabel: 'Keep',
        });
        if (confirmed) {
            await points.resetPoints();
        }
    };

    return (
        <section className={styles.section}>
            <div className={styles.rowLabel}>
                <span>Skill points</span>
                <span>{points.availablePoints} unspent of {points.earnedPoints}</span>
            </div>
            <Button
                variant="secondary"
                disabled={points.isSaving || spentPoints <= 0 || points.bankedValue < points.respecCost}
                onClick={() => { void reset(); }}
            >
                Return skill points - {points.respecCost} banked essence
            </Button>
            {points.error && <p className={styles.status}>{points.error}</p>}
        </section>
    );
}

function DialDetail({ dial, player }: { dial: SkillTreeDialModel; player: UIPartyCharacter | null }): JSX.Element {
    const details = describeSkillTreeDial(dial);
    const meta = dial.placement.branch === 'martial'
        ? `${Math.round(dial.percent)}% - ${resolveMartialTierName(dial.percent)}`
        : `${Math.round(dial.percent)}%`;
    return (
        <>
            <header className={styles.header}>
                <h2 className={styles.title}>{details.title}</h2>
                <span className={styles.meta}>{meta}</span>
            </header>

            <Separator />

            <p className={styles.blurb}>{details.blurb}</p>
            <p className={styles.status}>{details.status}</p>

            {dial.placement.branch === 'essence' && player && <VeilPointsSummary player={player} />}

            <ol className={styles.milestones}>
                {details.milestones.map((milestone) => (
                    <li key={milestone.percent} className={milestone.isReached ? styles.reached : undefined}>
                        <span>{milestone.percent}%</span>
                        <span>{milestone.label}</span>
                    </li>
                ))}
            </ol>
        </>
    );
}

function GroupDetail({ group }: { group: SkillTreeGroupModel }): JSX.Element {
    const details = describeSkillTreeGroup(group);
    return (
        <>
            <header className={styles.header}>
                <h2 className={styles.title}>{details.title}</h2>
                <span className={styles.meta}>{details.skills.length === 1 ? '1 skill' : `${details.skills.length} skills`}</span>
            </header>

            <Separator />

            <p className={styles.blurb}>{details.blurb}</p>

            <ol className={styles.milestones}>
                {details.skills.map((skill) => (
                    <li key={skill.id}>
                        <span>{skill.name}</span>
                        <span>{skill.levelText}</span>
                    </li>
                ))}
            </ol>
        </>
    );
}

function BranchOverview({ dials }: { dials: readonly SkillTreeDialModel[] }): JSX.Element {
    return (
        <>
            <header className={styles.header}>
                <h2 className={styles.title}>Skills</h2>
                <span className={styles.meta}>Select a skill, group, or dial for its details.</span>
            </header>

            <Separator />

            {dials.map((dial) => {
                const details = describeSkillTreeDial(dial);
                return (
                    <section key={dial.placement.branch} className={styles.section}>
                        <div className={styles.rowLabel}>
                            <span className={styles.branchName}>{details.title}</span>
                            <span>{Math.round(dial.percent)}%</span>
                        </div>
                        <p className={styles.status}>{details.status}</p>
                    </section>
                );
            })}
        </>
    );
}
